import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import './index.css'
/* Lapisan mesra mobile — WAJIB selepas index.css supaya peraturan @media
   (max-width:767px) di dalamnya menang. Desktop tidak terjejas. */
import './mobile.css'
import App from './App.jsx'
import MaintenanceGate from './components/MaintenanceGate'
import { bacaBahasaTersimpan } from './i18n'
import LangProvider from './i18n/LangProvider'
import { pasangDomGuard } from './utils/domGuard'

/* ⭐ PERISAI DOM — WAJIB dipasang di sini, sebelum React mount.

   Ia menghalang halaman daripada tumbang bila extension pelanggan
   (terjemahan Chrome, muat turun video, antivirus, Grammarly) menukar
   nod yang React sedang pegang — punca skrin "Component Crash" pada
   /workspace. Baca nota penuh dalam utils/domGuard.js.

   ⛔ JANGAN pindahkan ke dalam komponen React. Menampal `Node.prototype`
      selepas React mula membina pokoknya bermakna operasi terawal —
      iaitu cat pertama, tempat kemalangan ini paling kerap berlaku —
      lepas tanpa perlindungan. */
pasangDomGuard()

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 0.2,
  replaysOnErrorSampleRate: 1.0,
  replaysSessionSampleRate: 0.0,
})

// Load saved theme preference
const savedTheme = localStorage.getItem('theme');
if (savedTheme === 'dark') {
  document.documentElement.classList.add('dark');
} else if (savedTheme === 'light') {
  document.documentElement.classList.remove('dark');
}

/* Tema v3.9 — TERANG ialah lalai (keputusan user 2026-08-17; ia MEMBALIKKAN
   lalai gelap 15 Ogos). Kelas `s-light` dipasang MELAINKAN pengguna pernah
   memilih Gelap di Settings → Paparan. Dipasang di sini, sebelum React,
   supaya tiada kelipan tema pada cat pertama.

   ⚠️ Seni bina tokens.css TIDAK terbalik bersama keputusan ini: blok asas
      masih GELAP dan terang masih override `html.s-light`. Maknanya lalai
      terang bergantung sepenuhnya pada baris ini berjaya dijalankan — kalau
      ia gagal, halaman jatuh ke gelap. Sebab itu bacaan storan dibalut
      try/catch yang memihak kepada terang (mod privasi tertentu melemparkan
      ralat pada akses localStorage).

   Kunci `sesaat_theme_v39` berasingan daripada `theme` di atas (milik UI
   lama) — dan tiada satu pun selector UI lama membaca `html.s-light`, jadi
   UI lama tidak terkesan langsung. */
let temaV39Gelap = false;
try {
  temaV39Gelap = localStorage.getItem('sesaat_theme_v39') === 'dark';
} catch { /* storan disekat — kekal pada lalai terang */ }
if (!temaV39Gelap) {
  document.documentElement.classList.add('s-light');
}

/* Bahasa v3.9 — ENGLISH ialah lalai (keputusan user 2026-08-16);
   `ms` dipasang hanya bila pengguna pernah memilih Bahasa Melayu di
   Settings → Paparan. Dicap pada <html lang> di sini (sebelum React)
   supaya browser, pembaca skrin dan semakan ejaan tahu bahasa halaman
   sejak cat pertama. Kunci `sesaat_lang` berasingan daripada
   `sesaat_theme_v39` di atas — dua pilihan berbeza, jangan cantumkan. */
document.documentElement.setAttribute('lang', bacaBahasaTersimpan());

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p style={{padding:'2rem'}}>Ralat berlaku. Sila refresh halaman.</p>}>
      <BrowserRouter>
        <LangProvider>
          <App />
          {/* Maintenance gate (admin ON di /admin/announce) — di sini, bukan dalam
              App.jsx, supaya ia menutup SEMUA halaman log masuk termasuk skrin
              semakan lesen yang tersangkut bila server down. */}
          <MaintenanceGate />
        </LangProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
